/**
 * Liga os radares ao mapa: uma camada de imagem por radar (acima do satélite,
 * `Z.RADAR`), trocada imperativamente a cada quadro — o mesmo mecanismo das
 * camadas do GOES (`map/FrameLayer.ts`), que troca a imagem já decodificada
 * sem piscar e continua animando durante o arrasto.
 *
 * Regras, iguais às do satélite:
 * - o radar segue a imagem QUE ESTÁ NA TELA (`shownTimeOf`), não o quadro
 *   pedido — enquanto o satélite ainda decodifica, o radar não corre na frente;
 * - a imagem só é trocada quando a nova está pronta; enquanto baixa, a anterior
 *   fica (sem piscar) — mas só se for do mesmo produto e de um quadro vizinho
 *   (até 30 min): um salto no tempo esconde o radar até a varredura certa
 *   chegar, em vez de mostrar uma de horas antes;
 * - sem varredura a até 12 min do horário (ou se ela falhou de vez), o radar
 *   SOME naquele quadro — nunca uma varredura velha com o horário novo.
 */
import type OlMap from 'ol/Map';
import type { FrameState } from 'ol/Map';
import { getWidth } from 'ol/extent';
import type { FrameCache } from '../goes/frameCache';
import { subscribeSelector } from '../lib/store';
import { useLoopStore } from '../loop/store';
import { FrameImageLayer, FrameLayerRenderer, FrameSource } from '../map/FrameLayer';
import type CanvasImageLayerRenderer from 'ol/renderer/canvas/ImageLayer';
import { Z } from '../map/zIndex';
import { shownTimeOf, useTimeStore } from '../state/time';
import { SIPAM_ATRIBUICAO } from './api';
import { geometriaDe, radarCache, type LarguraRadar } from './cache';
import { radaresAVistaAgora, usePlanoRadar } from './motor';
import { urlDe } from './plano';
import { useRadarStore, type RadarNoQuadro } from './store';
import { varreduraParaQuadro } from './tempo';

/**
 * A partir daqui (pixels do aparelho por pixel do bitmap) a imagem está sendo
 * AMPLIADA: desenha sem suavização, com os pixels do radar nítidos. Suavizar
 * uma imagem classificada ampliada borra a fronteira entre as classes e
 * inventa cores que não estão na legenda. Reduzindo, a suavização fica — é
 * ela que evita o serrilhado ao afastar.
 */
const AMPLIACAO_NITIDA = 1.5;

/** Enquanto a varredura nova carrega, a anterior só fica se estiver a até isto no tempo. */
const VIZINHA_MS = 30 * 60_000;

class RadarLayerRenderer extends FrameLayerRenderer {
  protected override preRender(
    context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    frameState: FrameState,
  ): void {
    super.preRender(context, frameState);
    const wrapper = this.image;
    const img = wrapper?.getImage() as { width?: number } | undefined;
    if (!wrapper || !img?.width) return;
    const resImagem = getWidth(wrapper.getExtent()) / img.width;
    const escala = (resImagem / frameState.viewState.resolution) * frameState.pixelRatio;
    if (escala > AMPLIACAO_NITIDA) context.imageSmoothingEnabled = false;
  }
}

class RadarImageLayer extends FrameImageLayer {
  protected override createRenderer(): CanvasImageLayerRenderer {
    return new RadarLayerRenderer(this);
  }
}

interface Slot {
  id: string;
  layer: RadarImageLayer;
  source: FrameSource;
  /** Chave da geometria com que a camada foi montada (extensão). */
  geoChave: string;
  shownUrl: string | null;
  shownTime: number | null;
  /** Produto da imagem na tela: a de outro produto nunca fica "enquanto carrega". */
  shownProduto: string | null;
}

export class RadarLayerManager {
  private readonly slots = new Map<string, Slot>();
  private readonly unsubs: Array<() => void> = [];
  private readonly waiters = new Set<() => void>();
  private disposed = false;

  constructor(
    private readonly map: OlMap,
    private readonly cache: FrameCache<LarguraRadar> = radarCache,
  ) {
    const refresh = () => this.refresh();
    this.unsubs.push(
      subscribeSelector(usePlanoRadar, (s) => s.plano, () => {
        this.syncLayers();
        this.refresh();
      }),
      subscribeSelector(useTimeStore, (s) => shownTimeOf(s), refresh),
      subscribeSelector(useRadarStore, (s) => s.visivel, refresh),
      subscribeSelector(useRadarStore, (s) => s.opacidade, (o) => {
        for (const slot of this.slots.values()) slot.layer.setOpacity(o);
      }),
      subscribeSelector(useLoopStore, (s) => `${s.playing}|${s.exportLocked}`, refresh),
      cache.subscribe(refresh),
    );
    // Radares que entram ou saem da vista mudam de "fora" para "carregando".
    map.on('moveend', refresh);
    this.unsubs.push(() => map.un('moveend', refresh));
    this.syncLayers();
    this.refresh();
  }

  /** Uma camada por radar do plano, na extensão da imagem reprojetada. */
  private syncLayers(): void {
    const plano = usePlanoRadar.getState().plano;
    const ids = new Set(plano?.radares.map((r) => r.id) ?? []);
    for (const [id, slot] of this.slots) {
      const geo = geometriaDe(id);
      const geoChave = geo ? geo.extent.join(',') : '';
      if (ids.has(id) && slot.geoChave === geoChave) continue;
      this.removeSlot(slot);
    }
    const opacidade = useRadarStore.getState().opacidade;
    for (const r of plano?.radares ?? []) {
      if (this.slots.has(r.id)) continue;
      const geo = geometriaDe(r.id);
      if (!geo) continue;
      const source = new FrameSource(geo.extent, SIPAM_ATRIBUICAO);
      const layer = new RadarImageLayer({ source, visible: false, opacity: opacidade, className: 'ol-layer radar-layer' });
      layer.set('radar', r.id);
      layer.setZIndex(Z.RADAR);
      this.map.addLayer(layer);
      this.slots.set(r.id, {
        id: r.id,
        layer,
        source,
        geoChave: geo.extent.join(','),
        shownUrl: null,
        shownTime: null,
        shownProduto: null,
      });
    }
  }

  private removeSlot(slot: Slot): void {
    this.map.removeLayer(slot.layer);
    this.limpar(slot);
    this.slots.delete(slot.id);
  }

  /** Solta a imagem da tela (e o bitmap no cache). */
  private limpar(slot: Slot): void {
    if (slot.shownUrl) this.cache.unpin(slot.shownUrl);
    slot.source.setFrameImage(null);
    slot.shownUrl = null;
    slot.shownTime = null;
    slot.shownProduto = null;
  }

  /** URL (chave do cache) da varredura que casa com o instante `t`. */
  private alvo(id: string, t: number | null): { url: string; varredura: number } | null {
    const plano = usePlanoRadar.getState().plano;
    const r = plano?.radares.find((x) => x.id === id);
    if (!plano || !r || t === null) return null;
    // Normalmente `t` é um dos quadros do plano: reaproveita o casamento dele
    // (a URL está no plano, logo é baixada e entra na janela).
    let i = plano.quadros.indexOf(t);
    // Fora dos quadros (a imagem na tela é de uma camada sobreposta com outra
    // cadência, porque a base falhou): segue o quadro PEDIDO, que está no plano.
    if (i < 0) {
      const { index, frames } = useTimeStore.getState();
      if (frames[index] !== undefined && plano.quadros[index] === frames[index]) i = index;
    }
    if (i >= 0) {
      const url = r.urls[i];
      const varredura = r.tempos[i];
      return url && varredura !== null ? { url, varredura } : null;
    }
    // Plano desatualizado por um instante (a lista de quadros acabou de mudar).
    const k = varreduraParaQuadro(r.varreduras, t);
    return k < 0 ? null : { url: urlDe(r, r.varreduras[k]), varredura: r.varreduras[k] };
  }

  private refresh(): void {
    if (this.disposed) return;
    const { visivel, manifesto, produto } = useRadarStore.getState();
    const t = shownTimeOf(useTimeStore.getState());
    const noQuadro: Record<string, RadarNoQuadro> = {};
    const aVista = visivel ? radaresAVistaAgora() : new Set<string>();

    for (const slot of this.slots.values()) {
      if (!visivel) {
        slot.layer.setVisible(false);
        continue;
      }
      // Imagem de outro produto (outra escala de cores) não fica presa na tela.
      if (slot.shownProduto !== null && slot.shownProduto !== produto) this.limpar(slot);
      const alvo = this.alvo(slot.id, t);
      if (!alvo) {
        slot.layer.setVisible(false);
        noQuadro[slot.id] = { estado: 'sem', varredura: null };
        continue;
      }
      const image = this.cache.get(alvo.url);
      if (image) {
        if (alvo.url !== slot.shownUrl || image !== slot.source.getFrameImage()) {
          if (alvo.url !== slot.shownUrl) {
            this.cache.pin(alvo.url);
            if (slot.shownUrl) this.cache.unpin(slot.shownUrl);
          }
          slot.source.setFrameImage(image);
          slot.shownUrl = alvo.url;
          slot.shownTime = alvo.varredura;
          slot.shownProduto = produto;
        }
        slot.layer.setVisible(true);
        noQuadro[slot.id] = { estado: 'ok', varredura: alvo.varredura };
        continue;
      }
      if (this.cache.status(alvo.url) === 'error') {
        slot.layer.setVisible(false);
        noQuadro[slot.id] = { estado: 'falhou', varredura: alvo.varredura };
        continue;
      }
      // Ainda baixando/decodificando: a anterior continua (sem piscar) se for
      // de um quadro vizinho e o radar estiver à vista. Fora da vista a imagem
      // não é atualizada — ao arrastar até ele, apareceria uma de horas antes.
      const vizinha = slot.shownTime !== null && Math.abs(slot.shownTime - alvo.varredura) <= VIZINHA_MS;
      slot.layer.setVisible(slot.shownUrl !== null && vizinha && aVista.has(slot.id));
      noQuadro[slot.id] = { estado: aVista.has(slot.id) ? 'carregando' : 'fora', varredura: alvo.varredura };
    }
    // Radares do manifesto sem nenhum arquivo deste produto (Macapá, Tefé hoje).
    if (visivel) {
      for (const r of manifesto?.radares ?? []) {
        if (r.produtos[produto].t.length === 0) noQuadro[r.id] = { estado: 'semDados', varredura: null };
      }
    }
    this.publicar(noQuadro);
    if (this.waiters.size && this.isCurrentFrameReady()) for (const w of [...this.waiters]) w();
  }

  private publicar(next: Record<string, RadarNoQuadro>): void {
    const prev = useRadarStore.getState().noQuadro;
    const ks = Object.keys(next);
    const igual =
      ks.length === Object.keys(prev).length &&
      ks.every((k) => prev[k]?.estado === next[k].estado && prev[k]?.varredura === next[k].varredura);
    if (!igual) useRadarStore.setState({ noQuadro: next });
  }

  /**
   * O quadro exibido está certo em todos os radares À VISTA? (varredura daquele
   * horário na tela, ou radar escondido por não ter varredura/ter falhado).
   * Exportando, a imagem na tela tem de ser a do tamanho inteiro — e não a
   * reduzida que continua em `get()` enquanto a redecodificação não chega.
   */
  isCurrentFrameReady(): boolean {
    if (!useRadarStore.getState().visivel) return true;
    const t = shownTimeOf(useTimeStore.getState());
    const exportando = useLoopStore.getState().exportLocked;
    const aVista = radaresAVistaAgora();
    for (const slot of this.slots.values()) {
      if (!aVista.has(slot.id)) continue;
      const alvo = this.alvo(slot.id, t);
      if (!alvo) continue;
      if (this.cache.status(alvo.url) === 'error') continue;
      const naTela = slot.shownUrl === alvo.url && slot.layer.getVisible();
      const nitida = !exportando || (this.cache.readyAtRequestedWidth(alvo.url) && slot.source.getFrameImage() === this.cache.get(alvo.url));
      if (naTela && nitida) continue;
      return false;
    }
    return true;
  }

  whenCurrentFrameReady(): Promise<void> {
    if (this.isCurrentFrameReady()) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        this.waiters.delete(done);
        resolve();
      };
      this.waiters.add(done);
    });
  }

  dispose(): void {
    this.disposed = true;
    this.unsubs.forEach((u) => u());
    for (const slot of [...this.slots.values()]) this.removeSlot(slot);
    for (const w of [...this.waiters]) w();
  }
}
